class CartDiscount extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('[data-cart-discount-input]');
    this.applyButton = this.querySelector('[data-cart-discount-apply]');
    this.messageEl = this.querySelector('[data-cart-discount-message]');

    this.applyButton?.addEventListener('click', this.onApply.bind(this));
    this.input?.addEventListener('keydown', event => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.onApply(event);
      }
    });
    this.querySelectorAll('[data-cart-discount-remove]').forEach(btn => {
      btn.addEventListener('click', this.onRemove.bind(this));
    });

    this.consumePendingMessage();
  }

  consumePendingMessage() {
    try {
      const raw = sessionStorage.getItem('cartDiscountMessage');
      if (!raw) return;
      sessionStorage.removeItem('cartDiscountMessage');
      const { text, tone } = JSON.parse(raw);
      this.setMessage(text, tone);
    } catch (e) { /* noop */ }
  }

  setMessage(text, tone) {
    if (!this.messageEl) return;
    this.messageEl.textContent = text || '';
    this.messageEl.dataset.tone = tone || '';
    this.messageEl.hidden = !text;
  }

  setBusy(isBusy) {
    if (this.applyButton) this.applyButton.disabled = isBusy;
    this.querySelectorAll('[data-cart-discount-remove]').forEach(btn => {
      btn.disabled = isBusy;
    });
    this.classList.toggle('is-loading', isBusy);
  }

  async onApply(event) {
    event.preventDefault();
    const code = (this.input?.value || '').trim();

    if (!code) {
      this.setMessage('Please enter a discount code.', 'error');
      this.input?.focus();
      return;
    }

    await this.sendDiscount(code, { attemptedCode: code });
  }

  async onRemove(event) {
    event.preventDefault();
    await this.sendDiscount('', { removing: true });
  }

  async sendDiscount(discount, { attemptedCode = '', removing = false } = {}) {
    if (this.isBusy) return;
    this.isBusy = true;
    this.setBusy(true);
    this.setMessage('', '');

    const sections = ['cart-drawer', 'cart-counter'];

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          discount,
          sections,
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.description || errBody.message || 'Unable to update discount. Please try again.');
      }

      const cart = await response.json();

      if (removing) {
        sessionStorage.setItem(
          'cartDiscountMessage',
          JSON.stringify({ text: 'Discount removed.', tone: 'success' })
        );
        this.renderCartSections(cart);
        return;
      }

      if (this.isCodeApplied(cart, attemptedCode)) {
        sessionStorage.setItem(
          'cartDiscountMessage',
          JSON.stringify({ text: `Discount "${attemptedCode}" applied.`, tone: 'success' })
        );
        this.renderCartSections(cart);
        return;
      }

      this.setMessage(
        `"${attemptedCode}" isn't a valid or applicable discount.`,
        'error'
      );
    } catch (err) {
      this.setMessage(err.message || 'Something went wrong. Please try again.', 'error');
    } finally {
      this.isBusy = false;
      this.setBusy(false);
    }
  }

  isCodeApplied(cart, code) {
    if (!code || !cart) return false;
    const needle = code.toLowerCase();

    const codes = Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
    const codeMatch = codes.find(entry => (entry.code || '').toLowerCase() === needle);
    if (codeMatch) return codeMatch.applicable === true;

    const applied =
      cart.cart_level_discount_applications ||
      cart.discount_applications ||
      [];
    return applied.some(d => (d.title || d.code || '').toLowerCase() === needle);
  }

  renderCartSections(cart) {
    const sections = cart && cart.sections ? cart.sections : null;
    if (!sections) return;

    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function' && sections['cart-drawer']) {
      drawer.renderContents({ sections }, false);
      return;
    }

    const parser = new DOMParser();

    if (sections['cart-drawer']) {
      const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
      ['#CartDrawer-Header', '#CartDrawer-Body'].forEach(selector => {
        const next = doc.querySelector(selector);
        const current = document.querySelector(selector);
        if (next && current) current.innerHTML = next.innerHTML;
      });
    }

    if (sections['cart-counter']) {
      const doc = parser.parseFromString(sections['cart-counter'], 'text/html');
      const next = doc.querySelector('#shopify-section-cart-counter');
      const current = document.querySelector('#cart-counter');
      if (next && current) current.innerHTML = next.innerHTML;
    }

    if (typeof updateFreeShipping === 'function') updateFreeShipping();
  }
}

customElements.define('cart-discount', CartDiscount);
